import { useMemo } from 'react';

import { useBackend } from '@/hooks/useBackend';

/**
 * Derives a team's content path prefix from one of its page paths.
 *
 * Slug-based pages live under `/{slug}/...` and legacy pages under `/tpp/{teamId}/...`,
 * so the prefix is the leading `/tpp/{teamId}` when present, otherwise the first path segment.
 */
function derivePrefixFromPath(path: string, teamId: string): string {
  if (path === `/tpp/${teamId}` || path.startsWith(`/tpp/${teamId}/`)) {
    return `/tpp/${teamId}`;
  }

  const firstSegment = path.split('/').find(Boolean);
  return firstSegment ? `/${firstSegment}` : `/tpp/${teamId}`;
}

/**
 * Resolves the Developer Portal team's content path prefix at runtime.
 *
 * The team's content lives under `/content/{slug}/...`, and the slug is editable from the team
 * settings, so it must not be hardcoded. We derive the current prefix from the public
 * `/team-pages` endpoint by locating a page owned by the Developer Portal team (identified by the
 * build-time DEVELOPER_PORTAL_TEAM_ID) and reading the prefix off its path.
 *
 * `contentPrefix` is `undefined` until it resolves; callers should defer content fetches (pass a
 * null SWR key) until it is available rather than guessing, so a slug change can never produce a
 * broken request.
 */
export function useDeveloperPortalContentPrefix(): {
  contentPrefix: string | undefined;
  isLoading: boolean;
} {
  const { data, isLoading } = useBackend('/team-pages', {});

  const contentPrefix = useMemo(() => {
    const page = data?.find((teamPage) => teamPage.teamId === DEVELOPER_PORTAL_TEAM_ID);
    return page ? derivePrefixFromPath(page.path, DEVELOPER_PORTAL_TEAM_ID) : undefined;
  }, [data]);

  return { contentPrefix, isLoading };
}
